import { useRef, useState, useEffect } from "react";
import { Play, Pause, Download } from "lucide-react";

const BARS = 32;

export default function WaveformPlayer({ src, label = "Full Mix", downloadName, testId }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setCurrent(a.currentTime);
      setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
    };
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * a.duration;
  };

  return (
    <div className="flex items-center gap-3 bg-background/60 border border-border rounded-xl p-3" data-testid={testId}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        onClick={toggle}
        className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-105 transition-transform glow-primary"
        data-testid={testId ? `${testId}-play` : "player-play"}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-mono text-muted-foreground truncate">{label}</span>
          <span className="text-xs font-mono text-muted-foreground">{fmt(current)} / {fmt(duration)}</span>
        </div>
        <div className="relative h-8 cursor-pointer" onClick={seek}>
          <div className="absolute inset-0 flex items-center gap-[3px]">
            {Array.from({ length: BARS }).map((_, i) => {
              const active = (i / BARS) * 100 <= progress;
              return (
                <span
                  key={i}
                  className="flex-1 rounded-full origin-bottom"
                  style={{
                    height: `${20 + Math.abs(Math.sin(i * 1.3)) * 80}%`,
                    background: active ? "hsl(var(--primary))" : "hsl(var(--muted))",
                    animation: playing && active ? `eq 0.7s ease-in-out ${i * 0.03}s infinite` : "none",
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {src && (
        <a
          href={src}
          download={downloadName}
          className="shrink-0 w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
          data-testid={testId ? `${testId}-download` : "player-download"}
          aria-label="Download"
        >
          <Download size={16} />
        </a>
      )}
    </div>
  );
}
